import Glide from "@glidejs/glide";

import {
  Fancybox
} from "@fancyapps/ui";

import ru from "@fancyapps/ui/src/Fancybox/l10n/ru";

import {
  CustomActiveClass
} from "../../glide/components/custom-active-class.js";

import {
  VisibleClass
} from "../../glide/components/visible-class.js";

import {
  NavigationDisabler
} from "../../glide/components/navigation-disabler.js";

import {
  ResizeGap
} from "../../glide/components/resize-gap.js";

import {
  ControlsExtended
} from "../../glide/components/controls-extended.js";

import ContainerView from "../view/container.js";
import GalleryView from "../view/gallery.js";
import ThumbsView from "../view/thumbs.js";
import ThumbView from "../view/thumb.js";
import ControlsView from "../../slider/controls/view/controls.js";
import DotsNavigationView from "../../slider/dots-naviation/view/dots-navigation.js";

import ProductGalleryModel from "../model/product-gallery.js";

import {
  render,
  removeElement
} from "../../render/render.js";

import {
  debounce
} from "../../../utils/debounce.js";

import {
  modalSliderClosing
} from "../../../mixins/modal-slider-closing";

import {
  FancyboxOptions
} from "../../../const.js";

import {
  defaultProductGalleryModelData,
  KeyboardKey,
  NAVIGATION_CHANGE_MEDIA_QUERY,
  ActionType
} from "../const.js";
export default class ProductGallery {
  constructor(containerElement) {
    this._containerElement = containerElement;

    this._gallerySliderComponents = {
      'ControlsExtended': ControlsExtended,
      'NavigationDisabler': NavigationDisabler,
      'CustomActiveClass': CustomActiveClass
    };

    this._containerView = null;
    this._galleryView = null;
    this._galleryControlsView = null;
    this._thumbsView = null;
    this._thumbViews = [];
    this._dotsNavigationView = null;

    this._gallerySlider = null;
    this._thumbsSlider = null;

    this._model = null;

    this._gallerySliderRunAfterHandler = this._gallerySliderRunAfterHandler.bind(this);
    this._handleThumbClick = this._handleThumbClick.bind(this);
    this._handleThumbKeydown = this._handleThumbKeydown.bind(this);
    this._handleWindowResize = this._handleWindowResize.bind(this);
    this._handleChangeData = this._handleChangeData.bind(this);
  }

  init() {
    this._containerView = new ContainerView(this._containerElement);
    this._galleryView = new GalleryView(this._containerView.getGalleryElement());
    this._initGallerySlider();
    this._initThumbsSlider();
    this._initModel();

    window.addEventListener(`resize`, debounce(this._handleWindowResize));
  }

  _initModel() {
    const isLayoutChanged = (window.matchMedia(NAVIGATION_CHANGE_MEDIA_QUERY).matches) ?
      true : false;

    this._model = new ProductGalleryModel(Object.assign(
        {},
        defaultProductGalleryModelData,
        {
          isLayoutChanged
        }
    ));

    this._model.addObserver(this._handleChangeData);
  }

  _initGallerySlider() {
    if (this._gallerySlider) {
      this._gallerySlider.destroy();
      this._gallerySlider = null;

      removeElement(this._galleryControlsView.getElement());
      this._galleryControlsView = null;

      if (this._dotsNavigationView) {
        removeElement(this._dotsNavigationView.getElement());
        this._dotsNavigationView = null;
      }
    }

    this._gallerySlider = new Glide(this._galleryView.getElement(), {
      perView: 1,
      gap: 0,
      type: `slider`,
      bound: true,
      animationDuration: 300,
      rewind: false,
      keyboard: false
    });

    this._galleryControlsView = new ControlsView({
      postfix: `product-gallery`
    });

    render(this._galleryView.getElement(), this._galleryControlsView.getElement());

    this._initDotsNavigation();

    this._gallerySlider.on(`run`, this._gallerySliderRunAfterHandler);
    this._gallerySlider.mount(this._gallerySliderComponents);

    this._initModal();
  }

  _initDotsNavigation() {
    if (window.matchMedia(NAVIGATION_CHANGE_MEDIA_QUERY).matches && !this._dotsNavigationView) {
      this._dotsNavigationView = new DotsNavigationView(
          this._galleryView.getSlides().length,
          `product-title`
      );
      render(this._galleryView.getElement(), this._dotsNavigationView.getElement());
    }
  }

  _initThumbsSlider() {
    if (this._thumbsSlider) {
      this._thumbsSlider.destroy();
      this._thumbsSlider = null;
    }

    if (
      this._containerView.getThumbsElement() &&
      !window.matchMedia(NAVIGATION_CHANGE_MEDIA_QUERY).matches
    ) {
      this._thumbsView = new ThumbsView(this._containerView.getThumbsElement());
      this._thumbsSlider = new Glide(this._thumbsView.getElement(), {
        perView: 5,
        gap: 8,
        type: `slider`,
        animationDuration: 300,
        rewind: false,
        keyboard: false,
        bound: true,
        breakpoints: {
          1180: {
            perView: 6
          }
        }
      });

      this._thumbsSlider.mount({
        'VisibleClass': VisibleClass,
        'ResizeGap': ResizeGap
      });

      this._thumbsView.setActiveThumb();

      Array.from(this._thumbsView.getThumbs())
      .forEach((item, index) => {
        const view = new ThumbView(item, index);
        view.setClickHandler(this._handleThumbClick);
        view.setKeydownHandler(this._handleThumbKeydown);
        this._thumbViews.push(view);
      });
    }
  }

  _destroyThumbsSlider() {
    if (this._thumbsSlider) {
      this._thumbsSlider.destroy();
      this._thumbsSlider = null;
    }
  }

  _initModal() {
    const selector = this._galleryView.getLinksSelector();

    this._setModalSliderClosingData();

    Fancybox.defaults.l10n = ru;
    Fancybox.bind(selector, Object.assign(
        {},
        FancyboxOptions,
        {
          on: {
            closing: this.closingModalHandler.bind(this)
          }
        }
    ));
  }

  _setModalSliderClosingData() {
    this._slider = this._gallerySlider;
    this._wrapperView = this._galleryView;
  }

  _gallerySliderRunAfterHandler() {
    if (!this._thumbsSlider) {
      return;
    }

    const newIndex = this._gallerySlider.index;

    const perView = this._thumbsSlider.settings.perView;
    const totalSlides = this._thumbsView.getThumbs().length;

    if (totalSlides > perView) {
      if (totalSlides - newIndex >= perView) {
        this._thumbsSlider.go(`=${newIndex}`);
      } else {
        this._thumbsSlider.go(`>>`);
      }
    }

    this._thumbsView.setActiveThumb(newIndex);
  }

  _handleThumbClick(index) {
    this._gallerySlider.go(`=${index}`);
  }

  _handleThumbKeydown(evt, index) {
    if (evt.key === KeyboardKey.ENTER) {
      this._gallerySlider.go(`=${index}`);
    }
  }

  _handleWindowResize() {
    const modelData = this._model.getData();

    if (window.matchMedia(NAVIGATION_CHANGE_MEDIA_QUERY).matches && modelData.isLayoutChanged) {
      return;
    }

    if (!window.matchMedia(NAVIGATION_CHANGE_MEDIA_QUERY).matches && !modelData.isLayoutChanged) {
      return;
    }

    this._model.changeData(Object.assign(
        {},
        modelData,
        {
          isLayoutChanged: !modelData.isLayoutChanged
        }
    ), ActionType.CHANGE_LAYOUT);
  }

  _handleChangeData(action) {
    switch (action) {
      case ActionType.CHANGE_LAYOUT:
        this._initGallerySlider();
        this._initThumbsSlider();
        break;
    }
  }
}

Object.assign(ProductGallery.prototype, modalSliderClosing);
